/**
 * The two forms People and Clients share (Figma 09 · Form · Invite, Form ·
 * Add a client, Form · Edit brands).
 */
import { useState } from "react";
import { CheckboxGroup } from "react-aria-components";
import { useGetBrands } from "@/hooks/useGetBrands";
import type { AssignableRole, BrandRef } from "@/types/organization.type";
import { useSendInvite } from "@/app/routes/dashboard/settings/api/useOrganization";
import { KitCheckbox } from "../../kit/Checkbox";
import { KitRadioGroup, KitRadio } from "../../kit/Checkbox";
import { KitInput } from "../../kit/Input";
import { kitLabelClass } from "../../kit/Field";
import { KitModal } from "../../kit/Modal";
import { BrandMark } from "../../kit/BrandPicker";
import { DialogFooter, Note, QuietNote } from "./SettingsParts";
import { plural } from "./settingsFormat";

/** Brand rows with a tick each. The server refuses a client with none. */
export function BrandChecklist({ value, onChange }: { value: string[]; onChange: (ids: string[]) => void }) {
  const { data: brands, isLoading } = useGetBrands();
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" aria-hidden>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-9 animate-pulse rounded-row bg-co-hover" />
        ))}
      </div>
    );
  }
  if (!brands?.length) return <p className="text-[13px] text-co-muted">This workspace has no brands yet.</p>;
  return (
    <CheckboxGroup value={value} onChange={onChange} aria-label="Brands" className="flex flex-col gap-0.5">
      {brands.map((brand) => (
        <KitCheckbox key={brand.id} value={brand.id} className="rounded-row px-2 py-1.5 hover:bg-co-hover">
          <span className="inline-flex items-center gap-2.5">
            <BrandMark brand={brand} size={24} />
            <span className="text-[13.5px] font-medium text-co-text">{brand.name}</span>
          </span>
        </KitCheckbox>
      ))}
    </CheckboxGroup>
  );
}

const ROLE_CHOICES: { role: AssignableRole; label: string; body: (brands: number) => string }[] = [
  {
    role: "Contributor",
    label: "Contributor",
    body: (n) => `Plans, writes and schedules posts in ${n === 1 ? "the 1 brand" : `all ${n} brands`}. Takes a seat.`,
  },
  { role: "Viewer", label: "Viewer", body: (n) => `Sees ${n === 1 ? "the 1 brand" : `all ${n} brands`} and comments. Changes nothing. Takes a seat.` },
  { role: "Guest", label: "Client", body: () => "Sees only the brands you pick, for review. Takes no seat." },
];

/**
 * Invite someone (People) or give a client access (Clients, `clientOnly`).
 * Closes on confirm; the pending row shows at once and the hook toasts a refusal.
 */
export function InviteDialog({
  organizationId,
  organizationName,
  seatsLeft,
  maxSeats,
  clientOnly = false,
  initialBrandIds = [],
  onClose,
  onSent,
}: {
  organizationId: string | null;
  organizationName: string;
  /** Null when the workspace has no seat cap. */
  seatsLeft: number | null;
  maxSeats: number | null;
  clientOnly?: boolean;
  initialBrandIds?: string[];
  onClose: () => void;
  onSent?: (email: string) => void;
}) {
  const { data: brands } = useGetBrands();
  const sendInvite = useSendInvite(organizationId);
  const full = seatsLeft !== null && seatsLeft <= 0;
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AssignableRole>(clientOnly || full ? "Guest" : "Contributor");
  const [brandIds, setBrandIds] = useState<string[]>(initialBrandIds);
  const isClient = role === "Guest";
  const brandCount = brands?.length ?? 0;
  const trimmed = email.trim();
  const valid = /\S+@\S+\.\S+/.test(trimmed) && (!isClient || brandIds.length > 0) && (isClient || !full);

  const send = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const picked: BrandRef[] = (brands ?? []).filter((b) => brandIds.includes(b.id)).map(({ id, name }) => ({ id, name }));
    sendInvite.mutate({ email: trimmed, role, brands: isClient ? picked : undefined });
    onSent?.(trimmed.toLowerCase());
    onClose();
  };

  return (
    <KitModal
      isOpen
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={clientOnly ? "Give someone access" : `Invite someone to ${organizationName}`}
      description={
        clientOnly
          ? "They sign in to review what your team shares. A client takes no seat and never sees costs."
          : "They get an email from you, signed with your name. The invitation works for 7 days."
      }
      modalClassName="!w-[min(calc(100vw-2rem),520px)] !max-w-[520px]"
    >
      <form onSubmit={send} className="flex w-full flex-col gap-4">
        <KitInput
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={isClient ? "client@brand.com" : "teammate@company.com"}
          autoFocus
        />
        {!clientOnly ? (
          <div className="flex flex-col gap-2">
            <p className={kitLabelClass}>Role</p>
            <KitRadioGroup aria-label="Role" value={role} onChange={(v) => setRole(v as AssignableRole)} className="gap-1">
              {ROLE_CHOICES.map((choice) => (
                <KitRadio
                  key={choice.role}
                  value={choice.role}
                  isDisabled={full && choice.role !== "Guest"}
                  className="items-start rounded-row px-2 py-2 hover:bg-co-hover"
                >
                  <span className="flex flex-col gap-0.5">
                    <span className="text-[13.5px] font-semibold text-co-text">{choice.label}</span>
                    <span className="text-[12.5px] text-co-muted">{choice.body(brandCount)}</span>
                  </span>
                </KitRadio>
              ))}
            </KitRadioGroup>
          </div>
        ) : null}
        {isClient ? (
          <div className="flex flex-col gap-2">
            <p className={kitLabelClass}>Brands they can open</p>
            <BrandChecklist value={brandIds} onChange={setBrandIds} />
            <QuietNote>They get an email from you. Adding a brand later opens it at once.</QuietNote>
          </div>
        ) : full ? null : seatsLeft !== null ? (
          <QuietNote>This uses 1 of the {plural(seatsLeft, "seat")} left.</QuietNote>
        ) : null}
        {full && !clientOnly ? (
          <Note tone="alert">
            All {maxSeats} seats are in use. Ask Mohtawa for more seats, or invite this person as a Client, which
            takes no seat.
          </Note>
        ) : null}
        <DialogFooter
          type="submit"
          confirmLabel={clientOnly ? "Give access" : "Send invitation"}
          onCancel={onClose}
          disabled={!valid}
        />
      </form>
    </KitModal>
  );
}

/** Brands a client can open (Clients / 06, People client "Add a brand"). */
export function EditBrandsDialog({
  name,
  initial,
  onSave,
  onClose,
  title,
}: {
  name: string;
  initial: string[];
  onSave: (brandIds: string[]) => void;
  onClose: () => void;
  title?: string;
}) {
  const [value, setValue] = useState<string[]>(initial);
  return (
    <KitModal
      isOpen
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={title ?? `Brands ${name} can open`}
      description={`A brand you tick opens for ${name} at once. A brand you untick closes at once.`}
      modalClassName="!w-[min(calc(100vw-2rem),480px)] !max-w-[480px]"
    >
      <form
        className="flex w-full flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (value.length === 0) return;
          onSave(value);
          onClose();
        }}
      >
        <BrandChecklist value={value} onChange={setValue} />
        {value.length === 0 ? <QuietNote>A client needs at least one brand.</QuietNote> : null}
        <DialogFooter type="submit" confirmLabel="Save" onCancel={onClose} disabled={value.length === 0} />
      </form>
    </KitModal>
  );
}
